import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import type { ITagProps } from 'libs/design-system/src/types/tag/tag';
import { PageStatus } from '@unified-checkout/design-system';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { CUSTOMER_DETAILS_SECTION_ITEMS } from '@app/dashboard/constants/payment-links/single-payments';
import { SINGLE_PAYMENT_LINKS_EVENTS } from '@app/dashboard/constants/tracking_events/payment_links/single';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';

import { PaymentSection } from './sections/Payment';
import CustomerDetails from './sections/CustomerDetails';
import Restrictions from './sections/Restrictions';
import AdvancedOptions from './sections/AdvancedOptions';
import Footer from './sections/Footer';

import styles from './single-payment-link.module.scss';

export const SinglePaymentLink = () => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();

  const [fullPageStatus, setFullPageStatus] = useState({
    error: false,
    disabled: false,
    loading: true,
  });
  const [fields, setFields] = useState<IField>({});
  const [tags, setTags] = useState<ITagProps[]>([]);
  const [customerDetailsType, setCustomerDetailsType] = useState<string>(
    CUSTOMER_DETAILS_SECTION_ITEMS(t)[0].value
  );

  useEffect(() => {
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.PAYMENT_LINK_PAGE_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
    });
  }, []);

  return (
    <div
      className={styles['single-payment-links']}
      data-single-pl-creation-disabled={fullPageStatus.disabled}
    >
      {fullPageStatus.error ? (
        <PageStatus
          status="error"
          title={t('errors.somethingWentWrong')}
          icon={CircleExclamation}
        />
      ) : (
        <>
          <PaymentSection
            fields={fields}
            setFields={setFields}
            fullPageStatus={fullPageStatus}
            setFullPageStatus={setFullPageStatus}
          />
          <CustomerDetails
            fields={fields}
            setFields={setFields}
            tags={tags}
            setTags={setTags}
            fullPageStatus={fullPageStatus}
            customerDetailsType={customerDetailsType}
            setCustomerDetailsType={setCustomerDetailsType}
          />
          <Restrictions
            fields={fields}
            setFields={setFields}
            fullPageStatus={fullPageStatus}
            customerDetailsType={customerDetailsType}
          />
          <AdvancedOptions
            fields={fields}
            setFields={setFields}
            fullPageStatus={fullPageStatus}
          />
          <Footer
            fields={fields}
            tags={tags}
            fullPageStatus={fullPageStatus}
            setFullPageStatus={setFullPageStatus}
            customerDetailsType={customerDetailsType}
          />
        </>
      )}
    </div>
  );
};
